/**
 * ToolCustomizer.tsx - Interactive URL customizer with live preview
 * 
 * CHANGE HISTORY:
 * - 251122_1545 danielbeer: Refactored to use Configgr instead of localStorage
 * - 251122_1323 danielbeer: Created interactive customizer popover
 * 
 * Allows users to customize current tool settings and see live preview
 * Generates URL that matches exactly what they see
 * Now uses Configgr for persistent settings storage
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { ToolType } from '../types';
import { useConfig } from '../hooks/useConfig';

interface ToolCustomizerProps {
  currentTool: ToolType;
  onClose: () => void;
  onFieldViewChange?: (view: 'single' | 'multi') => void;
  onSamplesChange?: (enabled: boolean) => void;
  onNavVisibilityChange?: (showNav: boolean, showSidebar: boolean) => void;
  currentFieldView?: 'single' | 'multi';
  currentSamplesEnabled?: boolean;
  currentNavVisible?: boolean;
  currentSidebarVisible?: boolean;
}

type SubmissionMethod = 'script' | 'odata' | 'dapi';
type SaveMode = 'auto' | 'batch';

// Get submission example code for each tool and method
function getSubmissionExample(tool: string, method: SubmissionMethod, scriptName: string, endpoint: string): React.ReactNode {
  const examples: Record<string, Record<SubmissionMethod, string>> = {
    'outliner': {
      'script': `// FileMaker Script: ${scriptName}
// Get outliner data and send to FileMaker
const outlinerData = {
  nodes: [
    { id: '1', text: 'Task 1', parentId: null },
    { id: '2', text: 'Subtask', parentId: '1' }
  ]
};

// Call FileMaker script
window.FileMaker.PerformScript('${scriptName}', JSON.stringify(outlinerData));

// FileMaker Script (${scriptName}):
Set Variable [ $data ; Get ( ScriptParameter ) ]
Set Variable [ $nodes ; JSONGetElement ( $data ; "nodes" ) ]
// Loop through nodes and create records
Set Variable [ $i ; 0 ]
Loop
  Exit Loop If [ $i ≥ JSONGetElementCount ( $nodes ) ]
  Set Variable [ $node ; JSONGetElement ( $nodes ; "[" & $i & "]" ) ]
  Set Variable [ $text ; JSONGetElement ( $node ; "text" ) ]
  New Record/Request
  Set Field [ Tasks::Name ; $text ]
  Commit Records/Requests
  Set Variable [ $i ; $i + 1 ]
End Loop`,
      'odata': `// oData REST API submission
const outlinerData = {
  nodes: [
    { id: '1', text: 'Task 1', parentId: null },
    { id: '2', text: 'Subtask', parentId: '1' }
  ]
};

// POST to oData endpoint
fetch('${endpoint || 'https://server.com/fmi/odata/v4/database'}/Tasks', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Basic ' + btoa('username:password')
  },
  body: JSON.stringify({
    Name: outlinerData.nodes[0].text,
    ParentID: outlinerData.nodes[0].parentId
  })
}).then(response => response.json())
  .then(data => console.log('Created:', data));`,
      'dapi': `// FileMaker Data API submission
const outlinerData = {
  nodes: [
    { id: '1', text: 'Task 1', parentId: null },
    { id: '2', text: 'Subtask', parentId: '1' }
  ]
};

// Get auth token first
const token = await getDataAPIToken();

// Create record via Data API
fetch('${endpoint || 'https://server.com/fmi/data/v1/databases/MyDB'}/layouts/Tasks/records', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + token
  },
  body: JSON.stringify({
    fieldData: {
      Name: outlinerData.nodes[0].text,
      ParentID: outlinerData.nodes[0].parentId
    }
  })
}).then(response => response.json())
  .then(data => console.log('Created:', data));`
    },
    'field-matcher': {
      'script': `// FileMaker Script: ${scriptName}
// Get matched field data
const matchedData = {
  mappings: {
    'firstName': 'John',
    'lastName': 'Doe',
    'email': 'john@example.com'
  }
};

window.FileMaker.PerformScript('${scriptName}', JSON.stringify(matchedData));

// FileMaker Script (${scriptName}):
Set Variable [ $data ; Get ( ScriptParameter ) ]
Set Variable [ $mappings ; JSONGetElement ( $data ; "mappings" ) ]
Set Field [ Contacts::FirstName ; JSONGetElement ( $mappings ; "firstName" ) ]
Set Field [ Contacts::LastName ; JSONGetElement ( $mappings ; "lastName" ) ]
Set Field [ Contacts::Email ; JSONGetElement ( $mappings ; "email" ) ]
Commit Records/Requests`,
      'odata': `// oData - Update contact record
const matchedData = {
  firstName: 'John',
  lastName: 'Doe',
  email: 'john@example.com'
};

fetch('${endpoint || 'https://server.com/fmi/odata/v4/database'}/Contacts(123)', {
  method: 'PATCH',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Basic ' + btoa('username:password')
  },
  body: JSON.stringify({
    FirstName: matchedData.firstName,
    LastName: matchedData.lastName,
    Email: matchedData.email
  })
});`,
      'dapi': `// Data API - Update contact
const matchedData = {
  firstName: 'John',
  lastName: 'Doe',
  email: 'john@example.com'
};

const token = await getDataAPIToken();
fetch('${endpoint || 'https://server.com/fmi/data/v1/databases/MyDB'}/layouts/Contacts/records/123', {
  method: 'PATCH',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + token
  },
  body: JSON.stringify({
    fieldData: {
      FirstName: matchedData.firstName,
      LastName: matchedData.lastName,
      Email: matchedData.email
    }
  })
});`
    },
    'pdf-viewer': {
      'script': `// FileMaker Script: ${scriptName}
// Send current page info
const pdfInfo = {
  currentPage: 5,
  totalPages: 20,
  filename: 'document.pdf'
};

window.FileMaker.PerformScript('${scriptName}', JSON.stringify(pdfInfo));

// FileMaker Script (${scriptName}):
Set Variable [ $data ; Get ( ScriptParameter ) ]
Set Field [ Documents::CurrentPage ; JSONGetElement ( $data ; "currentPage" ) ]
Set Field [ Documents::TotalPages ; JSONGetElement ( $data ; "totalPages" ) ]
Commit Records/Requests`,
      'odata': `// oData - Update document progress
const pdfInfo = {
  currentPage: 5,
  totalPages: 20
};

fetch('${endpoint || 'https://server.com/fmi/odata/v4/database'}/Documents(456)', {
  method: 'PATCH',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Basic ' + btoa('username:password')
  },
  body: JSON.stringify({
    CurrentPage: pdfInfo.currentPage,
    TotalPages: pdfInfo.totalPages
  })
});`,
      'dapi': `// Data API - Update document
const pdfInfo = {
  currentPage: 5,
  totalPages: 20
};

const token = await getDataAPIToken();
fetch('${endpoint || 'https://server.com/fmi/data/v1/databases/MyDB'}/layouts/Documents/records/456', {
  method: 'PATCH',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + token
  },
  body: JSON.stringify({
    fieldData: {
      CurrentPage: pdfInfo.currentPage,
      TotalPages: pdfInfo.totalPages
    }
  })
});`
    }
  };

  const toolExamples = examples[tool] || examples['outliner'];
  const code = toolExamples[method] || 'No example available for this combination.';

  return <pre>{code}</pre>;
}

export const ToolCustomizer: React.FC<ToolCustomizerProps> = ({ 
  currentTool, 
  onClose,
  onFieldViewChange,
  onSamplesChange,
  onNavVisibilityChange,
  currentFieldView = 'single',
  currentSamplesEnabled = false,
  currentNavVisible = true,
  currentSidebarVisible = true
}) => {
  // Use Configgr for settings
  const { get, set } = useConfig();
  
  // Load settings from URL params or Configgr
  const loadSetting = (configKey: string, urlKey: string, defaultValue: string) => {
    const params = new URLSearchParams(window.location.search);
    const urlValue = params.get(urlKey);
    if (urlValue) return urlValue;
    
    return get(configKey, defaultValue);
  };

  const [fmScript, setFmScript] = useState(() => loadSetting('dataSubmission.fileMakerScript.defaultScriptName', 'fmScript', 'jsRunScript'));
  const [submissionMethod, setSubmissionMethod] = useState<SubmissionMethod>(() => 
    loadSetting('dataSubmission.submitMethod', 'submitMethod', 'script') as SubmissionMethod
  );
  const [odataEndpoint, setOdataEndpoint] = useState(() => 
    loadSetting('dataSubmission.odata.defaultEndpoint', 'odataEndpoint', '')
  );
  const [showSubmissionExample, setShowSubmissionExample] = useState(false);
  const [saveMode, setSaveMode] = useState<SaveMode>(() => 
    loadSetting('dataSubmission.saveMode', 'saveMode', 'auto') as SaveMode
  );
  const [customizeMode, setCustomizeMode] = useState(() => 
    loadSetting('navigation.showCustomizeButton', 'customize', 'false') === 'true'
  );
  const [fieldView, setFieldView] = useState<'single' | 'multi'>(() => 
    loadSetting('plugins.outliner.defaultView', 'fieldView', currentFieldView) as 'single' | 'multi'
  );
  const [showNavBar, setShowNavBar] = useState(() => 
    loadSetting('navigation.hideNav', 'hideNav', 'false') === 'false'
  );
  const [showSidebar, setShowSidebar] = useState(() => 
    loadSetting('navigation.hideSidebar', 'hideSidebar', 'false') === 'false'
  );
  const [samples, setSamples] = useState(() => 
    loadSetting('development.samplesEnabled', 'samples', currentSamplesEnabled ? 'true' : 'false') === 'true'
  );
  const [copied, setCopied] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Picker-specific options
  const [pickerMode, setPickerMode] = useState<'dropdown' | 'list' | 'menu' | 'modal'>(() => 
    loadSetting('plugins.picker.mode', 'pickerMode', 'dropdown') as 'dropdown' | 'list' | 'menu' | 'modal'
  );

  // Tag Manager-specific options
  const [tagMode, setTagMode] = useState<'compact' | 'full' | 'inline' | 'modal' | 'cloud'>(() => 
    loadSetting('plugins.tagManager.mode', 'mode', 'full') as 'compact' | 'full' | 'inline' | 'modal' | 'cloud'
  );

  // Persist settings to Configgr whenever they change
  useEffect(() => {
    set('dataSubmission.fileMakerScript.defaultScriptName', fmScript);
    set('dataSubmission.submitMethod', submissionMethod);
    set('dataSubmission.odata.defaultEndpoint', odataEndpoint);
    set('dataSubmission.saveMode', saveMode);
    set('plugins.outliner.defaultView', fieldView);
    set('navigation.hideNav', !showNavBar);
    set('navigation.hideSidebar', !showSidebar);
    set('development.samplesEnabled', samples);
    set('navigation.showCustomizeButton', customizeMode);
    set('plugins.picker.mode', pickerMode);
    set('plugins.tagManager.mode', tagMode);
  }, [set, fmScript, submissionMethod, odataEndpoint, saveMode, fieldView, showNavBar, showSidebar, samples, customizeMode, pickerMode, tagMode]);

  // Apply changes immediately (live preview)
  useEffect(() => {
    if (currentTool === 'outliner' && onFieldViewChange) {
      onFieldViewChange(fieldView);
    }
  }, [fieldView, currentTool, onFieldViewChange]);

  useEffect(() => {
    if (onSamplesChange) {
      onSamplesChange(samples);
    }
  }, [samples, onSamplesChange]);

  useEffect(() => {
    if (onNavVisibilityChange) {
      onNavVisibilityChange(showNavBar, showSidebar);
    }
  }, [showNavBar, showSidebar, onNavVisibilityChange]);

  // Apply Picker mode changes immediately (live preview)
  useEffect(() => {
    if (currentTool === 'picker') {
      // Update URL param to trigger re-render
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      if (pickerMode !== 'dropdown') {
        params.set('pickerMode', pickerMode);
      } else {
        params.delete('pickerMode');
      }
      const newUrl = `${url.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
      // Trigger a custom event to notify the component
      window.dispatchEvent(new CustomEvent('picker-mode-change', { detail: { mode: pickerMode } }));
    }
  }, [pickerMode, currentTool]);

  // Apply Tag Manager mode changes immediately (live preview)
  useEffect(() => {
    if (currentTool === 'tag-manager') {
      // Update URL param to trigger re-render
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);
      if (tagMode !== 'full') {
        params.set('mode', tagMode);
      } else {
        params.delete('mode');
      }
      const newUrl = `${url.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
      // Trigger a custom event to notify the component
      window.dispatchEvent(new CustomEvent('tag-mode-change', { detail: { mode: tagMode } }));
    }
  }, [tagMode, currentTool]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Get clean base URL
  const baseUrl = `${window.location.origin}${window.location.pathname}`;

  // Build URL with current parameters
  const buildURL = () => {
    const params = new URLSearchParams();
    
    // Always add tool parameter
    params.append('tool', currentTool);
    
    // Add tool-specific parameters
    if (currentTool === 'outliner') {
      params.append('fieldView', fieldView);
    }
    
    if (currentTool === 'picker' && pickerMode !== 'dropdown') {
      params.append('pickerMode', pickerMode);
    }
    
    if (currentTool === 'tag-manager' && tagMode !== 'full') {
      params.append('mode', tagMode);
    }
    
    // Navigation parameters
    if (!showNavBar) {
      params.append('hideNav', 'true');
    }
    if (showNavBar && !showSidebar) {
      params.append('hideSidebar', 'true');
    }
    
    // Customize mode (shows floating button when nav hidden)
    if (customizeMode) {
      params.append('customize', 'true');
    }
    
    if (samples) {
      params.append('samples', 'true');
    }
    
    if (fmScript !== 'jsRunScript') {
      params.append('fmScript', fmScript);
    }
    
    if (submissionMethod !== 'script') {
      params.append('submitMethod', submissionMethod);
    }
    
    if (odataEndpoint) {
      params.append('odataEndpoint', encodeURIComponent(odataEndpoint));
    }
    
    if (saveMode !== 'auto') {
      params.append('saveMode', saveMode);
    }
    
    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  };

  const generatedURL = buildURL();

  // Copy URL to clipboard
  const copyURL = () => {
    navigator.clipboard.writeText(generatedURL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Apply settings to current view
  const applySettings = () => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams();
    
    params.set('tool', currentTool);
    
    if (currentTool === 'outliner') {
      params.set('fieldView', fieldView);
    }
    
    if (!showNavBar) {
      params.set('hideNav', 'true');
    }
    if (showNavBar && !showSidebar) {
      params.set('hideSidebar', 'true');
    }
    
    // Customize mode
    if (customizeMode) {
      params.set('customize', 'true');
    }
    
    if (samples) {
      params.set('samples', 'true');
    }
    
    if (fmScript !== 'jsRunScript') {
      params.set('fmScript', fmScript);
    }
    
    if (submissionMethod !== 'script') {
      params.set('submitMethod', submissionMethod);
    }
    
    if (odataEndpoint) {
      params.set('odataEndpoint', encodeURIComponent(odataEndpoint));
    }
    
    if (saveMode !== 'auto') {
      params.set('saveMode', saveMode);
    }
    
    url.search = params.toString();
    window.location.href = url.toString();
  };

  return (
    <>
      <div 
        className="tool-customizer-window" 
        ref={popoverRef}
      >
        <div className="customizer-header">
          <h3>🎨 Customize {currentTool}</h3>
          <button 
            className="customizer-close-btn" 
            onClick={onClose}
          >×</button>
        </div>

        <div className="customizer-content">
        {/* Data Submission Section */}
        <div className="customizer-section">
          <h4>Data Submission</h4>
          
          <div className="customizer-field">
            <label>Submission Method</label>
            <select value={submissionMethod} onChange={(e) => setSubmissionMethod(e.target.value as SubmissionMethod)}>
              <option value="script">FileMaker Script</option>
              <option value="odata">oData REST API</option>
              <option value="dapi">FileMaker Data API</option>
            </select>
          </div>

          {submissionMethod === 'script' && (
            <div className="customizer-field">
              <label>Script Name</label>
              <input
                type="text"
                value={fmScript}
                onChange={(e) => setFmScript(e.target.value)}
                placeholder="jsRunScript"
              />
            </div>
          )}

          {(submissionMethod === 'odata' || submissionMethod === 'dapi') && (
            <div className="customizer-field">
              <label>API Endpoint</label>
              <input
                type="text"
                value={odataEndpoint}
                onChange={(e) => setOdataEndpoint(e.target.value)}
                placeholder="https://server.com/fmi/odata/v4/database"
              />
              <span className="field-hint">
                {submissionMethod === 'odata' ? 'oData endpoint URL' : 'Data API endpoint URL'}
              </span>
            </div>
          )}

          <button 
            className="example-button"
            onClick={() => setShowSubmissionExample(!showSubmissionExample)}
          >
            {showSubmissionExample ? '▼' : '▶'} Show {currentTool} Example
          </button>

          {showSubmissionExample && (
            <div className="example-code">
              {getSubmissionExample(currentTool, submissionMethod, fmScript, odataEndpoint)}
            </div>
          )}

          <div className="customizer-field" style={{ marginTop: '16px' }}>
            <label>Save Mode</label>
            <select value={saveMode} onChange={(e) => setSaveMode(e.target.value as SaveMode)}>
              <option value="auto">Auto-Save (changes saved immediately)</option>
              <option value="batch">Batch Save (manual save button)</option>
            </select>
            <span className="field-hint">
              {saveMode === 'auto' 
                ? 'Changes are saved automatically as you make them'
                : 'Changes are held until you click Save button'}
            </span>
          </div>
        </div>

        {/* Tool-specific options */}
        {currentTool === 'outliner' && (
          <div className="customizer-section">
            <h4>Outliner Options</h4>
            <div className="customizer-field">
              <label>Field View</label>
              <select value={fieldView} onChange={(e) => setFieldView(e.target.value as 'single' | 'multi')}>
                <option value="single">Single Field</option>
                <option value="multi">Multi Field</option>
              </select>
              <span className="field-hint">
                Single field for simple outlines, multi-field for structured data
              </span>
            </div>
          </div>
        )}

        {currentTool === 'picker' && (
          <div className="customizer-section">
            <h4>Picker Options</h4>
            <div className="customizer-field">
              <label>Display Mode</label>
              <select value={pickerMode} onChange={(e) => setPickerMode(e.target.value as typeof pickerMode)}>
                <option value="dropdown">Dropdown (Compact)</option>
                <option value="list">List View</option>
                <option value="menu">Full Menu</option>
                <option value="modal">Modal Dialog</option>
              </select>
              <span className="field-hint">
                Controls how the picker is displayed
              </span>
            </div>
            <div className="customizer-info">
              <strong>Sample Features:</strong>
              <ul>
                <li>• Keyboard navigation (arrows, enter, escape)</li>
                <li>• Real-time search/filter</li>
                <li>• Group selection (multi-select tags)</li>
                <li>• Max selection limits</li>
                <li>• Data source display</li>
              </ul>
            </div>
          </div>
        )}

        {currentTool === 'tag-manager' && (
          <div className="customizer-section">
            <h4>Tag Manager Options</h4>
            <div className="customizer-field">
              <label>Display Mode</label>
              <select value={tagMode} onChange={(e) => setTagMode(e.target.value as typeof tagMode)}>
                <option value="compact">Compact (Minimal)</option>
                <option value="full">Full (Complete UI)</option>
                <option value="inline">Inline (Single Line)</option>
                <option value="modal">Modal (Popup)</option>
                <option value="cloud">Cloud (Visualization)</option>
              </select>
              <span className="field-hint">
                Controls the tag manager interface style
              </span>
            </div>
            <div className="customizer-info">
              <strong>Sample Features:</strong>
              <ul>
                <li>• Tag cloud visualization</li>
                <li>• Add/remove tags</li>
                <li>• Tag suggestions</li>
                <li>• Sample data editor</li>
                <li>• Multiple sample datasets</li>
              </ul>
            </div>
          </div>
        )}

        {/* Navigation options */}
        <div className="customizer-section">
          <h4>Navigation Options</h4>
          <div className="customizer-field">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showNavBar}
                onChange={(e) => setShowNavBar(e.target.checked)}
              />
              Show Navigation Bar
            </label>
          </div>

          <div className="customizer-field">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showSidebar}
                onChange={(e) => setShowSidebar(e.target.checked)}
                disabled={!showNavBar}
              />
              Show Side Navigation
            </label>
          </div>
          
          <div className="customizer-field">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={customizeMode}
                onChange={(e) => setCustomizeMode(e.target.checked)}
              />
              Show Customize Button
            </label>
            <span className="field-hint">Show floating 🎨 button when nav is hidden</span>
          </div>
        </div>

        <div className="customizer-field">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={samples}
              onChange={(e) => setSamples(e.target.checked)}
            />
            Enable Samples Panel
          </label>
        </div>

        {/* Generated URL */}
        <div className="customizer-field-full">
          <label>Generated URL</label>
          <div className="url-display">
            <textarea
              value={generatedURL}
              readOnly
              rows={3}
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
            />
            <button onClick={copyURL} className="copy-btn">
              {copied ? '✓' : '📋'}
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="customizer-actions">
          <button onClick={copyURL} className="primary-btn">
            📋 Copy URL
          </button>
          <button onClick={applySettings} className="secondary-btn">
            🔄 Reload with Settings
          </button>
        </div>
        <div className="customizer-hint">
          ✨ Changes apply immediately - no reload needed!
        </div>
      </div>
    </div>

  <style>{`
        /* Self-contained tool window styles */
        .tool-customizer-window {
          position: fixed;
          bottom: 80px;
          right: 20px;
          width: min(450px, calc(100vw - 40px));
          max-height: calc(100vh - 120px);
          background: #ffffff;
          color: #1f2937;
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          border: 1px solid #e5e7eb;
          z-index: 99998;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          resize: both;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        
        .tool-customizer-window .customizer-header {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: move;
          background: #f9fafb;
          user-select: none;
        }
        
        .tool-customizer-window .customizer-header h3 {
          margin: 0;
          font-size: 0.875rem;
          font-weight: 600;
          color: #1f2937;
        }
        
        .tool-customizer-window .customizer-close-btn {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          padding: 0 0.25rem;
          color: #6b7280;
          line-height: 1;
        }
        
        .tool-customizer-window .customizer-close-btn:hover {
          color: #1f2937;
        }
        
        .tool-customizer-window .customizer-content {
          padding: 1rem;
          overflow: auto;
          flex: 1;
          font-size: 0.875rem;
        }
        
        .tool-customizer-window .customizer-section h4 {
          color: #1f2937;
          font-size: 0.875rem;
          font-weight: 600;
          margin: 0 0 0.75rem 0;
        }
        
        .tool-customizer-window label {
          color: #374151;
          font-weight: 500;
        }
        
        .tool-customizer-window select,
        .tool-customizer-window input[type="text"],
        .tool-customizer-window textarea {
          background: #ffffff;
          color: #1f2937;
          border: 1px solid #d1d5db;
        }
        
        .tool-customizer-window .field-hint {
          color: #6b7280;
        }
        
        .tool-customizer-window .customizer-hint {
          color: #6b7280;
        }
        
        /* Dark mode support */
        @media (prefers-color-scheme: dark) {
          .tool-customizer-window {
            background: #1f2937;
            color: #f9fafb;
            border-color: #374151;
          }
          
          .tool-customizer-window .customizer-header {
            background: #111827;
            border-bottom-color: #374151;
          }
          
          .tool-customizer-window .customizer-header h3 {
            color: #f9fafb;
          }
          
          .tool-customizer-window .customizer-close-btn {
            color: #9ca3af;
          }
          
          .tool-customizer-window .customizer-close-btn:hover {
            color: #f9fafb;
          }
          
          .tool-customizer-window .customizer-section h4 {
            color: #f9fafb;
          }
          
          .tool-customizer-window label {
            color: #e5e7eb;
          }
          
          .tool-customizer-window select,
          .tool-customizer-window input[type="text"],
          .tool-customizer-window textarea {
            background: #111827;
            color: #f9fafb;
            border-color: #4b5563;
          }
          
          .tool-customizer-window .field-hint {
            color: #9ca3af;
          }
          
          .tool-customizer-window .customizer-hint {
            color: #9ca3af;
          }
        }
        
        .tool-customizer-popover {
          position: absolute;
          top: 50px;
          right: 16px;
          width: 400px;
          max-height: 600px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          z-index: 1000;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .customizer-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .customizer-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
        }

        .close-btn {
          background: none;
          border: none;
          color: white;
          font-size: 24px;
          cursor: pointer;
          padding: 0;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .customizer-content {
          padding: 12px;
          overflow-y: auto;
          flex: 1;
        }

        .customizer-field {
          margin-bottom: 12px;
          display: grid;
          grid-template-columns: 140px 1fr;
          gap: 8px;
          align-items: center;
        }

        .customizer-field:last-child {
          margin-bottom: 0;
        }

        .customizer-field label {
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          margin: 0;
        }

        .customizer-field-full {
          margin-bottom: 12px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .customizer-field-full label {
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          margin: 0;
        }

        .customizer-field input[type="text"],
        .customizer-field select {
          width: 100%;
          padding: 6px 10px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 13px;
          color: #1f2937;
          transition: border-color 0.2s;
        }

        .customizer-field input[type="text"]:focus,
        .customizer-field select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 12px;
        }

        .checkbox-label input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        .field-hint {
          grid-column: 2;
          font-size: 11px;
          color: #9ca3af;
          margin-top: -8px;
          line-height: 1.3;
        }

        .field-hint {
          display: block;
          font-size: 0.75rem;
          color: var(--text-secondary, #6b7280);
          margin-top: 0.25rem;
        }

        .customizer-info {
          margin-top: 1rem;
          padding: 1rem;
          background: var(--surface-secondary, #f9fafb);
          border-radius: 8px;
          border: 1px solid var(--border-secondary, #e5e7eb);
        }

        .customizer-info strong {
          display: block;
          margin-bottom: 0.5rem;
          color: var(--text-primary, #1f2937);
          font-size: 0.875rem;
        }

        .customizer-info ul {
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .customizer-info li {
          font-size: 0.8125rem;
          color: var(--text-secondary, #6b7280);
          line-height: 1.6;
          padding: 0.125rem 0;
        }

        .url-display {
          display: flex;
          gap: 8px;
          align-items: flex-start;
        }

        .url-display textarea {
          flex: 1;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 11px;
          padding: 6px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          resize: vertical;
          line-height: 1.4;
          color: #1f2937;
          background: #f9fafb;
        }

        .url-display textarea:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .copy-btn {
          padding: 6px 10px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: background 0.2s;
          flex-shrink: 0;
          height: fit-content;
        }

        .copy-btn:hover {
          background: #2563eb;
        }

        .customizer-section {
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #e5e7eb;
        }

        .customizer-section h4 {
          margin: 0 0 8px 0;
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          grid-column: 1 / -1;
        }

        .customizer-actions {
          display: flex;
          gap: 8px;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
        }

        .customizer-hint {
          text-align: center;
          font-size: 12px;
          color: #10b981;
          margin-top: 8px;
          font-weight: 500;
        }

        .primary-btn,
        .secondary-btn {
          flex: 1;
          padding: 10px 16px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .primary-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .primary-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .secondary-btn {
          background: #f3f4f6;
          color: #374151;
        }

        .secondary-btn:hover {
          background: #e5e7eb;
        }

        .checkbox-label input[type="checkbox"]:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .checkbox-label:has(input:disabled) {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .example-button {
          width: 100%;
          padding: 8px 12px;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
          text-align: left;
          transition: all 0.2s;
          margin-top: 12px;
        }

        .example-button:hover {
          background: #e5e7eb;
          border-color: #9ca3af;
        }

        .example-code {
          margin-top: 12px;
          background: #1f2937;
          border-radius: 6px;
          padding: 12px;
          max-height: 300px;
          overflow-y: auto;
        }

        .example-code pre {
          margin: 0;
          color: #f9fafb;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 11px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        @media (prefers-color-scheme: dark) {
          .tool-customizer-popover {
            background: #1f2937;
            color: #f9fafb;
          }

          .customizer-header {
            border-bottom-color: #374151;
          }

          .customizer-field label {
            color: #f9fafb;
          }

          .customizer-field input[type="text"],
          .customizer-field select {
            background: #111827;
            border-color: #374151;
            color: #f9fafb;
          }

          .field-hint {
            color: #9ca3af;
          }

          .customizer-actions {
            border-top-color: #374151;
          }

          .secondary-btn {
            background: #374151;
            color: #f9fafb;
          }

          .secondary-btn:hover {
            background: #4b5563;
          }
      `}</style>
    </>
  );
};
